import React, { useEffect, useLayoutEffect, useRef, useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Slider, Box, Button } from '@mui/material';
import { RestartAlt } from '@mui/icons-material';
import * as d3 from 'd3';

const BOX_MARGIN = { top: 40, right: 30, bottom: 60, left: 70 };

// Clamp a 1-D [lo, hi] domain inside [bLo, bHi], preserving span where possible
// and enforcing a minimum span so the axis never collapses to a single value.
const clampDomain = ([lo, hi], [bLo, bHi]) => {
  const outerSpan = bHi - bLo;
  const minSpan = Math.min(outerSpan * 0.02 || 1e-6, outerSpan || 1e-6);
  let span = hi - lo;
  if (span < minSpan) span = minSpan;
  if (span >= outerSpan) return [bLo, bHi];
  let nLo = lo;
  let nHi = lo + span;
  if (nLo < bLo) { nLo = bLo; nHi = bLo + span; }
  if (nHi > bHi) { nHi = bHi; nLo = bHi - span; }
  return [nLo, nHi];
};

const MIN_WIDTH_PER_PLOT = 120;
const Y_SLIDER_COL = 48;

// Shared renderer used by both the on-screen (windowed) chart and the
// "Export Entire Chart" path. When `full` is true the whole band area is drawn
// with no horizontal clip/pan so the exported image contains every plot; the
// Y axis, X-axis title and legend are always drawn at fixed viewport positions
// so they stay put while the band area scrolls.
const drawBoxplot = ({
  svgNode,
  data,
  xAxisKey,
  showZScore,
  isPatientBiochemData,
  yAxisLabel,
  xAxisLabel,
  height,
  yDomain,
  colors,
  full = false,
  viewportWidth = 0,
  panX = 0,
  handlers = null,
}) => {
  const margin = BOX_MARGIN;
  const h = Math.max(height - margin.top - margin.bottom, 1);
  const contentPlotWidth = Math.max(data.length * MIN_WIDTH_PER_PLOT, 1);

  let svgWidth;
  let plotViewportWidth;
  let effectivePanX;
  let clip;
  let bandOffsetX = 0;
  if (full) {
    plotViewportWidth = contentPlotWidth;
    svgWidth = contentPlotWidth + margin.left + margin.right;
    effectivePanX = 0;
    clip = false;
  } else {
    svgWidth = Math.max(viewportWidth, margin.left + margin.right + 1);
    plotViewportWidth = Math.max(svgWidth - margin.left - margin.right, 1);
    const maxPan = Math.max(contentPlotWidth - plotViewportWidth, 0);
    effectivePanX = Math.min(Math.max(panX, 0), maxPan);
    clip = true;
    // When everything fits (no horizontal scroll), center the plots in the
    // available space instead of left-aligning them.
    if (maxPan <= 0) bandOffsetX = Math.max((plotViewportWidth - contentPlotWidth) / 2, 0);
  }

  const svg = d3.select(svgNode);
  svg.selectAll('*').remove();
  svg.attr('width', svgWidth).attr('height', height);

  const chart = svg.append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  const x = d3.scaleBand()
    .domain(data.map((d) => d[xAxisKey]))
    .range([0, contentPlotWidth])
    .padding(0.4);
  const y = d3.scaleLinear().domain(yDomain).range([h, 0]);

  // Grid lines first so plotted marks render on top of them.
  chart.append('g')
    .attr('class', 'grid')
    .selectAll('line')
    .data(y.ticks())
    .enter()
    .append('line')
    .attr('x1', 0)
    .attr('x2', plotViewportWidth)
    .attr('y1', (d) => y(d))
    .attr('y2', (d) => y(d))
    .attr('stroke', '#e0e0e0')
    .attr('stroke-width', 1);

  // Clipped, horizontally-panned band area. Marks are clipped to [0, h]; the
  // per-band tick labels get their own clip strip below the axis so they are
  // hidden horizontally when scrolled out of view but still shown below h.
  const uid = Math.random().toString(36).slice(2, 9);
  let marksParent = chart;
  let labelsParent = chart;
  if (clip) {
    const defs = svg.append('defs');
    defs.append('clipPath').attr('id', `bp-marks-${uid}`)
      .append('rect').attr('x', 0).attr('y', 0)
      .attr('width', plotViewportWidth).attr('height', h);
    defs.append('clipPath').attr('id', `bp-labels-${uid}`)
      .append('rect').attr('x', 0).attr('y', h)
      .attr('width', plotViewportWidth).attr('height', margin.bottom);
    marksParent = chart.append('g').attr('clip-path', `url(#bp-marks-${uid})`);
    labelsParent = chart.append('g').attr('clip-path', `url(#bp-labels-${uid})`);
  }
  const plotArea = marksParent.append('g')
    .attr('transform', `translate(${bandOffsetX - effectivePanX},0)`);
  const labelArea = labelsParent.append('g')
    .attr('transform', `translate(${bandOffsetX - effectivePanX},0)`);

  // Fixed Y axis (stays at the left while the band area scrolls).
  chart.append('g')
    .call(d3.axisLeft(y))
    .selectAll('text')
    .style('font-size', '14px');

  // Fixed X-axis baseline across the visible plot area.
  chart.append('line')
    .attr('x1', 0)
    .attr('x2', plotViewportWidth)
    .attr('y1', h)
    .attr('y2', h)
    .attr('stroke', '#000')
    .attr('stroke-width', 1);

  // Per-band tick labels scroll with the band area but sit below the axis.
  data.forEach((d) => {
    const center = x(d[xAxisKey]) + x.bandwidth() / 2;
    labelArea.append('line')
      .attr('x1', center).attr('x2', center)
      .attr('y1', h).attr('y2', h + 6)
      .attr('stroke', '#000').attr('stroke-width', 1);
    labelArea.append('text')
      .attr('text-anchor', 'middle')
      .attr('x', center)
      .attr('y', h + 20)
      .style('font-size', '14px')
      .style('font-family', 'Open Sans, Segoe UI, Helvetica Neue, Arial, sans-serif')
      .text(d[xAxisKey]);
  });

  // Axis titles (fixed: Y rotated at the left, X centered on the viewport).
  if (yAxisLabel) {
    chart.append('text')
      .attr('text-anchor', 'middle')
      .attr('transform', `translate(${-margin.left / 1.3}, ${h / 2}) rotate(-90)`)
      .style('font-size', '16px')
      .style('font-family', 'Open Sans, Segoe UI, Helvetica Neue, Arial, sans-serif')
      .text(yAxisLabel);
  }
  if (xAxisLabel) {
    chart.append('text')
      .attr('text-anchor', 'middle')
      .attr('x', plotViewportWidth / 2)
      .attr('y', h + margin.bottom * 0.85)
      .style('font-size', '16px')
      .style('font-family', 'Open Sans, Segoe UI, Helvetica Neue, Arial, sans-serif')
      .text(xAxisLabel);
  }

  const boxWidth = 40;
  const capWidth = 20;
  const strokeWidth = 2;
  const bind = (sel, d) => {
    if (!handlers) return sel;
    return sel
      .on('mouseover', (event) => handlers.onOver(event, d))
      .on('mousemove', handlers.onMove)
      .on('mouseout', handlers.onOut);
  };

  data.forEach((d) => {
    const center = x(d[xAxisKey]) + x.bandwidth() / 2;
    const whiskerMin = d.rendered_min ?? d.min;
    const whiskerMax = d.rendered_max ?? d.max;

    bind(plotArea.append('line')
      .attr('x1', center).attr('x2', center)
      .attr('y1', y(whiskerMin)).attr('y2', y(whiskerMax))
      .attr('stroke', colors.primary).attr('stroke-width', strokeWidth), d);
    bind(plotArea.append('line')
      .attr('x1', center - capWidth / 2).attr('x2', center + capWidth / 2)
      .attr('y1', y(whiskerMax)).attr('y2', y(whiskerMax))
      .attr('stroke', colors.primary).attr('stroke-width', strokeWidth), d);
    bind(plotArea.append('line')
      .attr('x1', center - capWidth / 2).attr('x2', center + capWidth / 2)
      .attr('y1', y(whiskerMin)).attr('y2', y(whiskerMin))
      .attr('stroke', colors.primary).attr('stroke-width', strokeWidth), d);
    bind(plotArea.append('rect')
      .attr('x', center - boxWidth / 2).attr('y', y(d.q3))
      .attr('height', y(d.q1) - y(d.q3)).attr('width', boxWidth)
      .attr('stroke', colors.primary).attr('fill', colors.secondary)
      .attr('stroke-width', 2), d);
    bind(plotArea.append('line')
      .attr('x1', center - boxWidth / 2).attr('x2', center + boxWidth / 2)
      .attr('y1', y(d.median)).attr('y2', y(d.median))
      .attr('stroke', colors.primary).attr('stroke-width', 2), d);

    if (!showZScore && d.mean != null) {
      bind(plotArea.append('circle')
        .attr('cx', center).attr('cy', y(d.mean)).attr('r', 7)
        .attr('stroke', colors.accentPrimary).attr('stroke-width', 3)
        .attr('fill', colors.accentSecondary), d);
    }
    if (showZScore && d.zScore != null) {
      bind(plotArea.append('rect')
        .attr('x', center - 6).attr('y', y(d.zScore) - 6)
        .attr('width', 12).attr('height', 12)
        .attr('fill', colors.accentSecondary)
        .attr('transform', `rotate(45, ${center}, ${y(d.zScore)})`)
        .attr('stroke', colors.accentPrimary).attr('stroke-width', 3), d);
    }
  });

  // Legend (fixed at the bottom-right of the visible area).
  const legend = svg.append('g')
    .attr('class', 'legend')
    .attr('transform', `translate(${svgWidth - 140}, ${height - 31})`);
  if (!showZScore) {
    legend.append('circle')
      .attr('cx', 15).attr('cy', 15).attr('r', 6)
      .attr('fill', colors.accentSecondary)
      .attr('stroke', colors.accentPrimary).attr('stroke-width', 3);
    legend.append('text')
      .attr('x', 30).attr('y', 20).attr('font-size', '12px').attr('fill', '#333')
      .style('font-family', 'Open Sans, Segoe UI, Helvetica Neue, Arial, sans-serif')
      .text('Mean');
  } else {
    legend.append('rect')
      .attr('x', 9).attr('y', 9).attr('width', 12).attr('height', 12)
      .attr('fill', colors.accentSecondary)
      .attr('transform', 'rotate(45, 15, 15)')
      .attr('stroke', colors.accentPrimary).attr('stroke-width', 3);
    legend.append('text')
      .attr('x', 30).attr('y', 20).attr('font-size', '12px').attr('fill', '#333')
      .style('font-family', 'Open Sans, Segoe UI, Helvetica Neue, Arial, sans-serif')
      .text('Patient Z-Score');
  }

  return {
    marginTop: margin.top,
    plotHeight: h,
    contentPlotWidth,
    plotViewportWidth,
    maxPan: full ? 0 : Math.max(contentPlotWidth - plotViewportWidth, 0),
    svgWidth,
  };
};

// Read the resolved chart colors from CSS custom properties.
const readChartColors = () => {
  const styles = getComputedStyle(document.documentElement);
  return {
    primary: styles.getPropertyValue('--primary-color').trim(),
    secondary: styles.getPropertyValue('--secondary-color').trim(),
    accentPrimary: styles.getPropertyValue('--accent-primary-color').trim(),
    accentSecondary: styles.getPropertyValue('--accent-secondary-color').trim(),
  };
};

// Serialize an <svg> node to a titled PNG and trigger a download.
const downloadSvgAsPng = (svgNode, { pngWidth, pngHeight, title }) => {
  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svgNode);
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = '16px "Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif';

    const maxLineWidth = pngWidth * 0.9;
    const words = (title || '').split(' ');
    const lines = [];
    let currentLine = words[0] || '';
    for (let i = 1; i < words.length; i++) {
      const testLine = `${currentLine} ${words[i]}`;
      if (ctx.measureText(testLine).width < maxLineWidth) {
        currentLine = testLine;
      } else {
        lines.push(currentLine);
        currentLine = words[i];
      }
    }
    lines.push(currentLine);

    const lineHeight = 20;
    const titleHeight = lines.length * lineHeight + 10;

    canvas.width = pngWidth;
    canvas.height = pngHeight + titleHeight;

    ctx.font = '16px "Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#000000';
    lines.forEach((line, i) => {
      ctx.fillText(line, canvas.width / 2, 24 + i * lineHeight);
    });
    ctx.drawImage(img, 0, titleHeight);
    URL.revokeObjectURL(url);

    const pngUrl = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = pngUrl;
    link.download = `ED3N-Boxplot-Chart-${(title || 'chart').replace(/\s+/g, '_')}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  img.src = url;
};

const BoxPlotChart = forwardRef(({ 
  data, 
  height = 400, 
  yAxisLabel, 
  xAxisLabel, 
  showZScore = false, 
  exportTitle = '', 
  xAxisKey,
  isPatientBiochemData = false
}, ref) => {
  const containerRef = useRef();
  const svgRef = useRef();
  const metricsRef = useRef(null);
  const ySliderRef = useRef(null);
  // Viewport width available to the svg (container minus the Y-slider column).
  const [width, setWidth] = useState(800);
  // User-driven Y zoom domain; null means "full range". Owned here so wheel,
  // drag, and slider stay in sync and Explore/modals inherit the behavior.
  const [userYDomain, setUserYDomain] = useState(null);
  // Horizontal pan offset (px) into the virtual band area.
  const [panX, setPanX] = useState(0);

  // Track the available width so the chart is windowed to the viewport (fixed
  // Y axis / centered X title / visible legend) instead of overflowing.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => setWidth(Math.max((el.offsetWidth || 800) - Y_SLIDER_COL, 200));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The vertical zoom Slider is a range (two thumbs): MUI renders the minimum
  // thumb (bottom) before the maximum thumb (top) in the DOM, so keyboard Tab
  // reaches the bottom handle first — the opposite of natural top-to-bottom
  // flow. Reverse the two thumbs' DOM order so Tab lands on the top (maximum)
  // handle first. Done via DOM order (not tabIndex) to avoid pulling the thumbs
  // to the front of the modal's focus cycle.
  useLayoutEffect(() => {
    const root = ySliderRef.current;
    if (!root) return;
    const minThumb = root.querySelector('.MuiSlider-thumb[data-index="0"]');
    const maxThumb = root.querySelector('.MuiSlider-thumb[data-index="1"]');
    if (minThumb && maxThumb && maxThumb.parentNode && minThumb.previousElementSibling !== maxThumb) {
      minThumb.parentNode.insertBefore(maxThumb, minThumb);
    }
  });

  // Full Y extent of the data (IQR-based, padded and "nice"-rounded). This is
  // the outer bound for zooming/panning.
  const dataYDomain = useMemo(() => {
    if (!data || data.length === 0) return [0, 1];
    const yValues = data.flatMap(d => [
      d.rendered_min ?? d.min,
      d.rendered_max ?? d.max,
      d.q1,
      d.q3,
      d.median,
      d.mean,
      showZScore ? d.zScore : null
    ].filter(v => v != null));
    const yMin = d3.min(yValues);
    const yMax = d3.max(yValues);
    const padding = (yMax - yMin || 1) * 0.1;
    return d3.scaleLinear().domain([yMin - padding, yMax + padding]).nice().domain();
  }, [data, showZScore]);

  const yDomain = userYDomain || dataYDomain;
  const plotAreaHeight = Math.max(height - BOX_MARGIN.top - BOX_MARGIN.bottom, 1);
  const ySliderStep = Math.max((dataYDomain[1] - dataYDomain[0]) / 200, 1e-6);

  // Horizontal windowing geometry (virtual band width vs. the visible viewport).
  const contentPlotWidth = Math.max((data?.length || 0) * MIN_WIDTH_PER_PLOT, 1);
  const plotViewportWidth = Math.max(width - BOX_MARGIN.left - BOX_MARGIN.right, 1);
  const maxPan = Math.max(contentPlotWidth - plotViewportWidth, 0);
  const clampedPanX = Math.min(Math.max(panX, 0), maxPan);

  // Reset zoom/pan when the underlying data or mode changes.
  useEffect(() => {
    setUserYDomain(null);
    setPanX(0);
  }, [data, showZScore, xAxisKey]);

  useImperativeHandle(ref, () => ({
    // Default export is WYSIWYG: it serializes the live (windowed) svg, so the
    // downloaded image matches what is currently scrolled/zoomed on screen.
    exportAsPNG: () => {
      if (!svgRef.current) return;
      downloadSvgAsPng(svgRef.current, {
        pngWidth: metricsRef.current?.svgWidth || width,
        pngHeight: height,
        title: exportTitle,
      });
    },
    // Export the entire chart at full horizontal size and reset (un-zoomed)
    // Y range, regardless of the current on-screen scroll/zoom.
    exportEntireAsPNG: () => {
      if (!data || data.length === 0) return;
      const svgNode = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svgNode.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      const metrics = drawBoxplot({
        svgNode,
        data,
        xAxisKey,
        showZScore,
        isPatientBiochemData,
        yAxisLabel,
        xAxisLabel,
        height,
        yDomain: dataYDomain,
        colors: readChartColors(),
        full: true,
      });
      downloadSvgAsPng(svgNode, {
        pngWidth: metrics.svgWidth,
        pngHeight: height,
        title: exportTitle,
      });
    },
  }));

  // Draw the on-screen (windowed) chart and wire the tooltip handlers.
  useEffect(() => {
    if (!data || data.length === 0) return undefined;

    const tooltipId = `d3-tooltip-${Math.random().toString(36).substring(2, 9)}`;
    const tooltip = d3.select('body')
      .append('div')
      .attr('id', tooltipId)
      .attr('class', 'd3-tooltip')
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('background-color', 'rgba(0,0,0,0.7)')
      .style('color', 'white')
      .style('padding', '6px 8px')
      .style('border-radius', '4px')
      .style('font-size', '12px')
      .style('visibility', 'hidden')
      .style('z-index', 9999);

    const handlers = {
      onOver: (event, d) => {
        const title = isPatientBiochemData && d.descAndShortName
          ? d.descAndShortName
          : d[xAxisKey];
        const hasRenderedValues = d.rendered_min != null && d.rendered_max != null;
        const hasOutliers = hasRenderedValues && (d.min !== d.rendered_min || d.max !== d.rendered_max);
        tooltip.style('visibility', 'visible').html(`
          <strong>${title}</strong><br/>
          ${showZScore && d.zScore != null ? `Patient Z-Score: ${d.zScore}<br/><br/>` : ''}
          ${d.mean != null ? `Mean: ${d.mean}<br/><br/>` : ''}
          Min: ${hasRenderedValues ? d.rendered_min : d.min}<br/>
          Q1: ${d.q1}<br/>
          Median: ${d.median}<br/>
          Q3: ${d.q3}<br/>
          Max: ${hasRenderedValues ? d.rendered_max : d.max}<br/>
          ${hasOutliers ? `<br/><em>Outliers: ${d.min} to ${d.max}</em>` : ''}
          ${d.mean != null && !hasOutliers ? `<br/>Mean: ${d.mean}` : ''}
        `);
      },
      onMove: (event) => {
        tooltip
          .style('top', (event.pageY + 10) + 'px')
          .style('left', (event.pageX + 10) + 'px');
      },
      onOut: () => tooltip.style('visibility', 'hidden'),
    };

    // Publish plot geometry so the wheel/drag handlers can map screen pixels to
    // data values (and horizontal pan bounds) without re-running the full draw.
    metricsRef.current = {
      ...drawBoxplot({
        svgNode: svgRef.current,
        data,
        xAxisKey,
        showZScore,
        isPatientBiochemData,
        yAxisLabel,
        xAxisLabel,
        height,
        yDomain,
        colors: readChartColors(),
        full: false,
        viewportWidth: width,
        panX: clampedPanX,
        handlers,
      }),
      yDomain,
      outer: dataYDomain,
    };

    return () => {
      if (!tooltip.empty()) tooltip.remove();
    };
  }, [data, width, height, showZScore, xAxisKey, isPatientBiochemData, userYDomain, dataYDomain, clampedPanX, yAxisLabel, xAxisLabel]);

  // Mouse-wheel zoom (Y only), anchored on the value under the cursor.
  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return undefined;
    const onWheel = (event) => {
      const m = metricsRef.current;
      if (!m) return;
      event.preventDefault();
      const rect = svgEl.getBoundingClientRect();
      const py = (event.clientY - rect.top) - m.marginTop;
      const frac = Math.max(0, Math.min(1, py / m.plotHeight));
      const [lo, hi] = m.yDomain;
      const anchor = hi - frac * (hi - lo); // range is [h, 0]: top = max
      const factor = event.deltaY > 0 ? 1.15 : 1 / 1.15;
      const nextLo = anchor - (anchor - lo) * factor;
      const nextHi = anchor + (hi - anchor) * factor;
      setUserYDomain(clampDomain([nextLo, nextHi], m.outer));
    };
    svgEl.addEventListener('wheel', onWheel, { passive: false });
    return () => svgEl.removeEventListener('wheel', onWheel);
  }, [data]);

  // Click-and-drag: horizontal pans the band area (updates panX), vertical
  // pans the zoomed Y range. Vertical panning is coalesced per animation frame.
  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return undefined;
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    let pendingDY = 0;
    let rafId = null;

    const flush = () => {
      rafId = null;
      if (!pendingDY) return;
      const m = metricsRef.current;
      if (!m) { pendingDY = 0; return; }
      const span = m.yDomain[1] - m.yDomain[0];
      const dyData = (pendingDY * span) / m.plotHeight;
      pendingDY = 0;
      // Drag down -> reveal larger values (add, because the Y range is inverted),
      // keeping the grabbed value under the cursor.
      const next = clampDomain([m.yDomain[0] + dyData, m.yDomain[1] + dyData], m.outer);
      if (next[0] !== m.yDomain[0] || next[1] !== m.yDomain[1]) setUserYDomain(next);
    };
    const onDown = (event) => {
      dragging = true;
      lastX = event.clientX;
      lastY = event.clientY;
      svgEl.style.cursor = 'grabbing';
      event.preventDefault();
    };
    const onMove = (event) => {
      if (!dragging) return;
      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      lastX = event.clientX;
      lastY = event.clientY;
      if (dx) {
        const mp = metricsRef.current ? metricsRef.current.maxPan : 0;
        setPanX((p) => Math.min(Math.max(p - dx, 0), mp));
      }
      pendingDY += dy;
      if (rafId === null) rafId = requestAnimationFrame(flush);
    };
    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      svgEl.style.cursor = 'grab';
      if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
      flush();
    };

    svgEl.style.cursor = 'grab';
    svgEl.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', endDrag);
    return () => {
      svgEl.removeEventListener('mousedown', onDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', endDrag);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [data]);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {(!data || data.length === 0) ? (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: `${height}px`,
          fontSize: '18px',
          color: '#666',
          fontFamily: '"Open Sans", "Segoe UI", "Helvetica Neue", Arial, sans-serif'
        }}>
          No Data Available
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', width: '100%' }}>
            <div
              style={{
                flex: '0 0 auto',
                width: Y_SLIDER_COL,
                display: 'flex',
                justifyContent: 'center',
                paddingTop: BOX_MARGIN.top,
                paddingBottom: BOX_MARGIN.bottom,
                boxSizing: 'border-box',
              }}
            >
              <Slider
                orientation="vertical"
                min={dataYDomain[0]}
                max={dataYDomain[1]}
                value={clampDomain(yDomain, dataYDomain)}
                onChange={(_event, value) => setUserYDomain(clampDomain(value, dataYDomain))}
                step={ySliderStep}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => Number(value).toFixed(2)}
                getAriaLabel={(index) =>
                  index === 0 ? 'Boxplot zoom minimum' : 'Boxplot zoom maximum'
                }
                sx={{ height: plotAreaHeight }}
                ref={ySliderRef}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <svg ref={svgRef} xmlns="http://www.w3.org/2000/svg" />
            </div>
          </div>
          {maxPan > 0 && (
            <Box
              sx={{
                pl: `${Y_SLIDER_COL + BOX_MARGIN.left}px`,
                pr: `${BOX_MARGIN.right}px`,
                mt: 0.5,
              }}
            >
              <Slider
                min={0}
                max={maxPan}
                value={clampedPanX}
                onChange={(_event, value) => setPanX(Math.min(Math.max(value, 0), maxPan))}
                step={Math.max(Math.round(MIN_WIDTH_PER_PLOT / 2), 1)}
                aria-label="Scroll chart horizontally"
                sx={{
                  '& .MuiSlider-thumb': {
                    width: 90,
                    height: 14,
                    borderRadius: '7px',
                    '&:hover, &.Mui-focusVisible': {
                      boxShadow: '0 0 0 6px rgba(25, 118, 210, 0.16)',
                    },
                  },
                  // Hide the "filled" portion of the track: for a scrollbar the
                  // thumb itself indicates position, so the highlight is noise.
                  '& .MuiSlider-track': {
                    display: 'none',
                  },
                }}
              />
            </Box>
          )}
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RestartAlt />}
              onClick={() => { setUserYDomain(null); setPanX(0); }}
              disabled={!userYDomain && clampedPanX === 0}
            >
              Reset Zoom
            </Button>
          </Box>
        </>
      )}
    </div>
  );
});

export default BoxPlotChart;
